/**
 * SF-4651 / SF-4729: the transcript files chosen to send, each checked here first (type, size,
 * count); a refused one is never sent. Used by the wizard's last step and by a build's page while
 * it waits for its transcripts.
 */
import { Form, List, Tag, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { MAX_TRANSCRIPT_FILES, TRANSCRIPT_EXTENSIONS } from '../constants';
import { addTranscripts, removeTranscript, type ChosenTranscript } from '../transcripts';

export interface TranscriptPickerProps {
  transcripts: ChosenTranscript[];
  onTranscriptsChange: (next: ChosenTranscript[]) => void;
  /** Files can no longer be changed (being sent, or sent). */
  locked: boolean;
  /** When a chosen file will be sent. */
  pendingHint: string;
  /** Transcripts the build already holds (SF-4729): they use up places of the five. */
  held?: number;
}

const pickerLabel = (held: number) => {
  if (held === 0) return `Call transcripts (up to ${MAX_TRANSCRIPT_FILES})`;
  const left = Math.max(0, MAX_TRANSCRIPT_FILES - held);
  return `Call transcripts (${left} of ${MAX_TRANSCRIPT_FILES} places left, ${held} already read)`;
};

function statusOf(t: ChosenTranscript, pendingHint: string) {
  if (t.refusal) return { tag: <Tag color="error">Refused</Tag>, detail: t.refusal };
  return { tag: <Tag>Not sent yet</Tag>, detail: pendingHint };
}

export const TranscriptPicker: React.FC<TranscriptPickerProps> = ({
  transcripts,
  onTranscriptsChange,
  locked,
  pendingHint,
  held = 0,
}) => (
  <>
    <Form.Item label={pickerLabel(held)}>
      <Upload.Dragger
        // Names the file input itself (antd drops a label's link to it while it is disabled).
        aria-label={pickerLabel(held)}
        multiple
        disabled={locked || held >= MAX_TRANSCRIPT_FILES}
        accept={TRANSCRIPT_EXTENSIONS.join(',')}
        showUploadList={false}
        // Keep the files here; the page sends them (antd never uploads them itself).
        beforeUpload={(_file, fileList) => {
          if (_file === fileList[0])
            onTranscriptsChange(addTranscripts(transcripts, fileList, held));
          return Upload.LIST_IGNORE;
        }}
        data-testid="transcript-dropzone"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drop transcript files here</p>
        <p className="ant-upload-hint">{TRANSCRIPT_EXTENSIONS.join(' ')}, up to 10 MB each</p>
      </Upload.Dragger>
    </Form.Item>
    {transcripts.length > 0 && (
      <List
        size="small"
        bordered
        dataSource={transcripts}
        renderItem={(t) => {
          const { tag, detail } = statusOf(t, pendingHint);
          return (
            <List.Item
              data-testid="transcript-row"
              actions={
                locked
                  ? []
                  : [
                      <Typography.Link
                        key="remove"
                        onClick={() =>
                          onTranscriptsChange(removeTranscript(transcripts, t.uid, held))
                        }
                      >
                        Remove
                      </Typography.Link>,
                    ]
              }
            >
              <List.Item.Meta title={t.file.name} description={detail} />
              {tag}
            </List.Item>
          );
        }}
      />
    )}
  </>
);
